/**
 * Static, non-interactive renderer for `MapArrow` items inside an export SVG.
 * Mirrors the visual output of `MapArrows.tsx` (color, dashing, arrowheads)
 * without any selection/draft logic.
 *
 * Arrowheads are rendered as separate filled `<polygon>` elements rather than
 * SVG markers so the stroke body can be explicitly shortened to end at the
 * arrowhead base — preventing the stroke from "poking through" the triangle.
 */

import type { MapArrow } from '@warplan/shared';
import { getArrowColor } from '../lib/category-colors.js';

const STROKE_PX = 4.2;
const DASH_ON = 20;
const DASH_OFF = 16;

function pointsAttr(points: MapArrow['points']): string {
  return points.map((p) => `${p.x},${p.y}`).join(' ');
}

interface ArrowTriangle {
  tip: { x: number; y: number };
  b1: { x: number; y: number };
  b2: { x: number; y: number };
}

/** Shortens `pts` by `amount` world-units from the end. */
function shortenFromEnd(
  pts: ReadonlyArray<MapArrow['points'][0]>,
  amount: number,
): MapArrow['points'] {
  const result: MapArrow['points'] = [...pts];
  let remaining = amount;
  while (result.length >= 2 && remaining > 0) {
    const last = result[result.length - 1]!;
    const prev = result[result.length - 2]!;
    const dx = last.x - prev.x;
    const dy = last.y - prev.y;
    const len = Math.hypot(dx, dy);
    if (len <= remaining) {
      result.pop();
      remaining -= len;
    } else {
      const t = 1 - remaining / len;
      result[result.length - 1] = { x: prev.x + t * dx, y: prev.y + t * dy };
      break;
    }
  }
  return result;
}

/** Shortens `pts` by `amount` world-units from the start. */
function shortenFromStart(
  pts: ReadonlyArray<MapArrow['points'][0]>,
  amount: number,
): MapArrow['points'] {
  const reversed = shortenFromEnd([...pts].reverse(), amount);
  return reversed.reverse();
}

/**
 * Computes a filled triangle for an arrowhead at the end (or start) of `pts`.
 * Returns `null` if the geometry is degenerate.
 */
function computeArrowTriangle(
  pts: ReadonlyArray<MapArrow['points'][0]>,
  fromEnd: boolean,
  halfBase: number,
  length: number,
): ArrowTriangle | null {
  if (pts.length < 2) return null;
  const tip = (fromEnd ? pts[pts.length - 1] : pts[0])!;
  const anchor = (fromEnd ? pts[pts.length - 2] : pts[1])!;
  const dx = tip.x - anchor.x;
  const dy = tip.y - anchor.y;
  const mag = Math.hypot(dx, dy);
  if (mag === 0) return null;
  const ux = dx / mag;
  const uy = dy / mag;
  const bx = tip.x - ux * length;
  const by = tip.y - uy * length;
  return {
    tip,
    b1: { x: bx - uy * halfBase, y: by + ux * halfBase },
    b2: { x: bx + uy * halfBase, y: by - ux * halfBase },
  };
}

function triAttr(t: ArrowTriangle): string {
  return `${t.tip.x},${t.tip.y} ${t.b1.x},${t.b1.y} ${t.b2.x},${t.b2.y}`;
}

export interface ExportArrowsProps {
  arrows: ReadonlyArray<MapArrow>;
  /**
   * Multiplier applied to stroke/dash sizes. Defaults to 1.2.
   * Used by exports that focus on a small area (e.g. a single hex) to keep
   * arrows visually proportional to the rendered viewport.
   */
  sizeScale?: number;
}

export function ExportArrows({ arrows, sizeScale = 1.2 }: ExportArrowsProps): React.JSX.Element {
  const strokeWidth = STROKE_PX * sizeScale;
  // Arrowhead geometry: halfBase=2×SW, length=4×SW.
  // Matches the markerWidth=4/markerHeight=4 proportions from the previous
  // marker-based approach, but without the "stroke pokes through tip" artifact.
  const ahHalfBase = strokeWidth * 2;
  const ahLength = ahHalfBase * 2;
  const dashArray = `${DASH_ON * sizeScale} ${DASH_OFF * sizeScale}`;

  return (
    <g data-layer="export-arrows" pointerEvents="none">
      {arrows.map((a) => {
        if (a.points.length < 2) return null;
        const color = getArrowColor(a);

        // Compute triangles from original (unshortened) points.
        const endHead = a.arrowheadEnd
          ? computeArrowTriangle(a.points, true, ahHalfBase, ahLength)
          : null;
        const startHead = a.arrowheadStart
          ? computeArrowTriangle(a.points, false, ahHalfBase, ahLength)
          : null;

        // Shorten the stroke body so it ends at the arrowhead base.
        let pts: MapArrow['points'] = a.points;
        if (endHead) pts = shortenFromEnd(pts, ahLength);
        if (startHead) pts = shortenFromStart(pts, ahLength);

        return (
          <g key={a.id}>
            <polyline
              points={pointsAttr(pts)}
              fill="none"
              stroke={color}
              strokeWidth={strokeWidth}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDasharray={a.dashed ? dashArray : undefined}
            />
            {startHead ? <polygon points={triAttr(startHead)} fill={color} /> : null}
            {endHead ? <polygon points={triAttr(endHead)} fill={color} /> : null}
          </g>
        );
      })}
    </g>
  );
}

